// The standard lockup, pale pill and all, is what the wireframe puts on this
// bar rather than the mono-white cut: at 30px in a 52px row the pill is what
// separates the mark from the deep purple behind it.
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { LogOut, User } from "lucide-react";
import praxisPilotMark from "@/assets/logo-praxis-pilot.png";
import { usePastConsentGate } from "@/components/consent/ConsentGate";
import { FeedbackButton } from "@/components/feedback/FeedbackButton";
import { Menu, MenuIdentity, MenuItem } from "@/components/ui/Menu";
import { useAuth } from "@/contexts/AuthContext";
import { Switcher, type SwitcherChoice } from "@/components/ui/Switcher";
import { HeaderCommandPalette } from "./HeaderCommandPalette";
import { MaybeLink } from "./MaybeLink";
import { TrailStep } from "./wayfinding";

/** The shell's header row, in pixels; published to the page under it as a variable. */
export const SHELL_HEADER_HEIGHT = 52;

/** One step of the header's trail: a place the attorney can go back to, or the page itself. */
export interface Crumb {
  label: string;
  /** Where the step leads. The last step, the page being read, has none. */
  to?: string;
  /**
   * The others this step could be, each keyed by where it leads, and which of
   * them it is. Drawn only when there is another to switch to.
   */
  switcher?: {
    name: string;
    choices: readonly SwitcherChoice[];
    current: string;
  };
}

const TRAIL_LINK = "truncate text-white/55 transition-colors hover:text-white";

/** One step of the trail: a link or the page being read, and its switch where it has one. */
function TrailCrumb({ crumb }: { crumb: Crumb }) {
  const navigate = useNavigate();
  const switcher =
    crumb.switcher !== undefined && crumb.switcher.choices.length > 1
      ? crumb.switcher
      : undefined;
  const choose = (to: string) => {
    void navigate(to);
  };

  if (crumb.to === undefined) {
    return switcher === undefined ? (
      <span aria-current="page" className="truncate text-white/75">
        {crumb.label}
      </span>
    ) : (
      <Switcher
        name={switcher.name}
        label={crumb.label}
        choices={switcher.choices}
        currentKey={switcher.current}
        onChoose={choose}
        current
        className="text-white/75 transition-colors hover:text-white"
      />
    );
  }
  return (
    <>
      <MaybeLink to={crumb.to} className={TRAIL_LINK}>
        {crumb.label}
      </MaybeLink>
      {switcher !== undefined && (
        <Switcher
          name={switcher.name}
          choices={switcher.choices}
          currentKey={switcher.current}
          onChoose={choose}
          className="p-0.5 text-white/40 transition-colors hover:text-white"
        />
      )}
    </>
  );
}

/**
 * The signed-in attorney's account behind their initial: who is signed in, and
 * Log out. The session knows the attorney by address only, so the address is
 * what the menu shows and its first letter is the mark.
 */
function SignedInAccount() {
  const { user, logout } = useAuth();
  if (user === null) return null;
  const address = user.email ?? "";
  const mark =
    address === "" ? (
      <User aria-hidden className="size-4" />
    ) : (
      address[0].toUpperCase()
    );
  return (
    <Menu name="Your account" trigger={{ content: mark, avatar: true }}>
      <MenuIdentity mark={mark} name={address === "" ? "Signed in" : address}>
        <MenuItem
          title="Log out"
          icon={<LogOut aria-hidden />}
          danger
          onSelect={logout}
        />
      </MenuIdentity>
    </Menu>
  );
}

/** Drawn past the legal gate only, as the palette and the feedback pill are. */
function AccountMenu() {
  return usePastConsentGate() ? <SignedInAccount /> : null;
}

/**
 * The 52px shell header, spanning both grid columns.
 *
 * It reports where the attorney is, from their clients down to the open draft.
 * Its controls are the trail and, at the right end, search, the feedback
 * button, drawn only past the legal gate, and the account. The public policy
 * pages wear this header without the feedback button. The Terminology toggle
 * goes before the feedback button, as the design draws it.
 */
export function V2Header({
  crumbs,
  terminology,
}: {
  crumbs: readonly Crumb[];
  /** The Terminology toggle, on a draft's pages; the shell owns its state. */
  terminology?: ReactNode;
}) {
  return (
    <header className="col-span-full flex h-[var(--shell-header-h)] items-center gap-3 border-b border-sidebar-border bg-sidebar px-4">
      <img
        src={praxisPilotMark}
        alt="Praxis Pilot"
        className="h-[30px] shrink-0"
      />
      {crumbs.length > 0 && (
        <>
          <span aria-hidden className="h-4 w-px shrink-0 bg-sidebar-border" />
          <nav aria-label="Breadcrumb" className="min-w-0">
            <ol className="flex min-w-0 items-center gap-1.5 text-sm">
              {crumbs.map((crumb, index) => (
                <li
                  key={`${crumb.label}-${String(index)}`}
                  className="flex min-w-0 items-center gap-1.5"
                >
                  {index > 0 && <TrailStep />}
                  <TrailCrumb crumb={crumb} />
                </li>
              ))}
            </ol>
          </nav>
        </>
      )}
      <span aria-hidden className="ml-auto" />
      <HeaderCommandPalette />
      {terminology}
      <FeedbackButton />
      <AccountMenu />
    </header>
  );
}
